var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useBackHandler } from '@react-native-community/hooks';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { accessTokenAtom, useMe } from '@/atoms/Auth';
import KeyboardAwareFormScrollView from '@/components/KeyboardAwareFormScrollView';
import { ScreenLayout } from '@/components/ScreenLayout';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useAppComponents from '@/hooks/useAppComponents';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import ProgressViewer from '@/screens/SignupScreen/components/ProgressViewer';
import ToastUtils from '@/utils/ToastUtils';
import SignupBasicPage from './SignupBasicProfilePage';
import SignupMobilityToolPage from './SignupMobilityToolPage';
import SignupPhonePage from './SignupPhonePage';
import { useUpdateUser } from './hooks/useUpdateUser';
const TOTAL_STEPS = 3;
export default function SignupScreen({ route, navigation, }) {
    const { api } = useAppComponents();
    const setAccessToken = useSetAtom(accessTokenAtom);
    const { setUserInfo } = useMe();
    const isKeyboardVisible = useKeyboardVisible();
    const [initialNickname, setInitialNickname] = useState('');
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    useEffect(() => {
        const fetchInitialNickname = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const response = yield api.getUserInfoGet({
                    headers: { Authorization: `Bearer ${route.params.token}` },
                });
                setInitialNickname((_a = response.data.user.nickname) !== null && _a !== void 0 ? _a : '');
            }
            catch (_b) {
                setInitialNickname('');
            }
            finally {
                setIsLoadingUser(false);
            }
        });
        fetchInitialNickname();
    }, []);
    const { formValue, updateField, formState, submit } = useUpdateUser({
        accessToken: route.params.token,
        enforceBirthYearRange: true,
    });
    // 닉네임 pre-fill은 최초 1회만. 사용자가 X로 지우면 다시 채우지 않는다.
    const hasPrefilledRef = React.useRef(false);
    useEffect(() => {
        if (!isLoadingUser && initialNickname && !hasPrefilledRef.current) {
            hasPrefilledRef.current = true;
            updateField('nickname', initialNickname);
        }
    }, [isLoadingUser, initialNickname]);
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // step1 휴대폰 인증 상태 — UserPhoneForm에서 올라옴
    const [isCodeInputStep, setIsCodeInputStep] = useState(false);
    const [isVerifyButtonActive, setIsVerifyButtonActive] = useState(false);
    const verifyHandlerRef = React.useRef(null);
    // 인증이 막 완료된 순간(false→true 전이)에만 자동으로 step2 전환.
    // 이미 인증된 채로 step1에 돌아온 경우는 자동 전환하지 않고, 하단 "다음" 버튼으로 진행한다.
    const prevVerifiedRef = React.useRef(false);
    useEffect(() => {
        if (formValue.isPhoneVerified && !prevVerifiedRef.current && step === 1) {
            setStep(2);
        }
        prevVerifiedRef.current = formValue.isPhoneVerified;
    }, [formValue.isPhoneVerified, step]);
    const progress = Math.round((step / TOTAL_STEPS) * 100);
    const isFirstFormValid = formValue.nickname !== '' &&
        formValue.email !== '' &&
        formValue.birthYear !== '' &&
        formState.nickname === 'VALID' &&
        formState.email === 'VALID' &&
        formState.birthYear === 'VALID';
    const handleBack = useCallback(() => {
        if (step === 1) {
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            return;
        }
        setStep(prev => prev - 1);
    }, [step, navigation]);
    // 헤더 백버튼 커스터마이즈
    useEffect(() => {
        navigation.setOptions({ onBackPress: handleBack });
        return () => {
            navigation.setOptions({ onBackPress: undefined });
        };
    }, [navigation, handleBack]);
    // 안드로이드 하드웨어 백버튼 처리
    useBackHandler(() => {
        handleBack();
        return true;
    }, [handleBack]);
    function signup() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (isSubmitting)
                return;
            try {
                setIsSubmitting(true);
                const accessToken = route.params.token;
                const user = yield submit();
                // 회원 정보 업데이트 후, 기기에 토큰을 저장한다.
                setAccessToken(accessToken);
                yield setUserInfo(user);
                if (route.params.asModal) {
                    navigation.goBack();
                }
                else {
                    navigation.replace('GuideForFirstVisit');
                }
            }
            catch (e) {
                let msg = '회원가입중 오류가 발생했습니다.';
                if (axios.isAxiosError(e)) {
                    const data = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
                    if (data === null || data === void 0 ? void 0 : data.msg) {
                        msg = data.msg;
                    }
                }
                ToastUtils.show(msg);
            }
            finally {
                setIsSubmitting(false);
            }
        });
    }
    const getButtonConfig = () => {
        switch (step) {
            case 1:
                // step1 = 휴대폰 인증
                if (formValue.isPhoneVerified) {
                    // 인증 완료: 하단 버튼 = 다음 (재진입 시에도 진행 가능)
                    return {
                        text: '다음',
                        disabled: false,
                        onPress: () => setStep(2),
                        rightLabel: '',
                        elementName: 'signup_step1_next',
                    };
                }
                if (isCodeInputStep) {
                    // 인증번호 입력 단계: 하단 버튼 = 인증번호 확인
                    return {
                        text: '인증번호 확인',
                        disabled: !isVerifyButtonActive,
                        onPress: () => { var _a; return (_a = verifyHandlerRef.current) === null || _a === void 0 ? void 0 : _a.call(verifyHandlerRef); },
                        rightLabel: '',
                        elementName: 'signup_phone_verify',
                    };
                }
                // 전화번호 입력 단계: 버튼 숨김(아직 인증 시작 전)
                return {
                    text: '',
                    disabled: true,
                    onPress: () => { },
                    rightLabel: '',
                    elementName: 'signup_phone_verify',
                    hidden: true,
                };
            case 2:
                // step2 = 기본정보
                return {
                    text: '다음',
                    disabled: !isFirstFormValid,
                    onPress: () => setStep(3),
                    rightLabel: '',
                    elementName: 'signup_step2_next',
                };
            case 3:
                // step3 = 이동유형
                return {
                    text: '가입하기',
                    disabled: isSubmitting || formValue.mobilityTools.length === 0,
                    onPress: signup,
                    rightLabel: formValue.mobilityTools.length > 0
                        ? `${formValue.mobilityTools.length} 개`
                        : '',
                    elementName: 'signup_step3_submit',
                };
            default:
                return {
                    text: '',
                    disabled: true,
                    onPress: () => { },
                    rightLabel: '',
                    elementName: 'signup_submit',
                };
        }
    };
    const buttonConfig = getButtonConfig();
    const scrollViewRef = React.useRef(null);
    // 키보드 위 도킹 푸터/컨테이너 배경색 (버튼과 동일 → 키보드 코너 radius 틈도 같은 색으로 채움)
    const footerBg = buttonConfig.disabled
        ? isKeyboardVisible
            ? '#E3E4E8'
            : color.gray15v2
        : color.brand40;
    // 스텝 전환 시 스크롤 최상단으로 리셋
    useEffect(() => {
        var _a;
        (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollToTop();
    }, [step]);
    // step1(휴대폰 인증)은 항상 마운트해두고 비활성 시 display:none으로 숨긴다.
    // → step2/3로 갔다가 돌아와도 인증 완료 UI 상태(인증번호 입력/성공)가 그대로 보존된다.
    const renderPages = () => (_jsxs(_Fragment, { children: [_jsx(View, { style: { display: step === 1 ? 'flex' : 'none' }, children: _jsx(SignupPhonePage, { formValue: formValue, formState: formState, updateField: updateField, accessToken: route.params.token, onCodeInputStepChange: setIsCodeInputStep, onVerifyActiveChange: setIsVerifyButtonActive, onVerifyReset: () => updateField('isPhoneVerified', false), onVerifyRequest: handler => {
                        verifyHandlerRef.current = handler;
                    } }) }), step === 2 && (_jsx(SignupBasicPage, { formValue: formValue, formState: formState, updateField: updateField })), step === 3 && (_jsx(SignupMobilityToolPage, { formValue: formValue, updateField: updateField, onSubmit: signup }))] }));
    return (_jsx(ScreenLayout, { isHeaderVisible: true, isKeyboardAvoidingView: true, safeAreaEdges: ['bottom'], children: _jsxs(View, { className: "flex-1 bg-white", children: [_jsx(View, { className: "px-[20px]", children: _jsx(ProgressViewer, { progress: progress }) }), _jsx(KeyboardAwareFormScrollView, { ref: scrollViewRef, className: "bg-white", contentContainerStyle: { paddingBottom: 40 }, children: renderPages() }), !buttonConfig.hidden && (
                // 키보드 떴을 때: 풀폭 플랫 바(좌우여백/라운드 없이 키보드 위 도킹).
                // 내렸을 때: 좌우 20 여백 + rounded-8 플로팅 버튼. (Figma 2439-34293/33927)
                _jsx(View, { style: isKeyboardVisible
                        ? { backgroundColor: footerBg }
                        : {
                            paddingHorizontal: 20,
                            paddingVertical: 10,
                            backgroundColor: color.white,
                        }, children: _jsxs(SccTouchableOpacity, { elementName: buttonConfig.elementName, logParams: { button_text: buttonConfig.text }, onPress: buttonConfig.disabled ? () => { } : buttonConfig.onPress, disabled: buttonConfig.disabled, style: {
                            height: 56,
                            width: '100%',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: isKeyboardVisible ? 0 : 8,
                            backgroundColor: footerBg,
                        }, children: [_jsx(Text, { style: {
                                    fontFamily: font.pretendardSemibold,
                                    fontSize: 18,
                                    lineHeight: 26,
                                    letterSpacing: -0.36,
                                    color: buttonConfig.disabled
                                        ? isKeyboardVisible
                                            ? color.gray40
                                            : color.gray30v2
                                        : color.white,
                                }, children: buttonConfig.text }), buttonConfig.rightLabel ? (_jsx(Text, { style: {
                                    position: 'absolute',
                                    right: 20,
                                    fontFamily: font.pretendardMedium,
                                    fontSize: 14,
                                    color: buttonConfig.disabled ? color.gray30v2 : color.white,
                                }, children: buttonConfig.rightLabel })) : null] }) }))] }) }));
}
